/**
 * P-16/P-17/P-23 管理员、API Key、会话管理（docs/03-UIUX.md §5）。
 *
 * Tab 切换：管理员列表 / API Key / 会话。
 * - 管理员：owner 专属的创建（含初始密码）、改角色、停用/启用；停用最后一个 owner 由后端 9010 兜底。
 * - API Key：owner 专属创建（明文**只出现一次**，服务端不保存）、撤销（T2）。
 * - 会话：踢下线（T1），当前会话不可踢自己。
 */
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../icons/Icon';
import { EmptyFirstRun, ErrorBlock } from '../components/DataState';
import { describeError } from '../api/client';
import {
  createAdminUser,
  createApiKey,
  deleteAdminUser,
  listAdminUsers,
  listApiKeys,
  listSessions,
  revokeAllSessions,
  revokeApiKey,
  revokeSession,
  updateAdminUser,
} from '../api/endpoints';
import type { AdminUser, ApiKey, Session } from '../api/types';
import { useAsync } from '../lib/useAsync';
import { ICON_SIZE } from '../tokens';
import { formatDate, formatDateTime } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { PASSWORD_MIN_LENGTH, SCOPE_MATRIX } from '../api/types';

type Tab = 'admins' | 'api-keys' | 'sessions';

const TABS: { key: Tab; label: string }[] = [
  { key: 'admins', label: '管理员' },
  { key: 'api-keys', label: 'API Key' },
  { key: 'sessions', label: '会话' },
];

export function IamPage() {
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';
  const [tab, setTab] = useState<Tab>('admins');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const admins = useAsync(useCallback(() => listAdminUsers(), []), []);
  const apiKeys = useAsync(useCallback(() => listApiKeys(), []), []);
  const sessions = useAsync(useCallback(() => listSessions(), []), []);

  const adminList: readonly AdminUser[] = admins.state.kind === 'ready' ? admins.state.data : [];
  const apiKeyList: readonly ApiKey[] = apiKeys.state.kind === 'ready' ? apiKeys.state.data : [];
  const sessionList: readonly Session[] = sessions.state.kind === 'ready' ? sessions.state.data : [];

  const flash = (kind: 'ok' | 'error', text: string) => {
    setError(kind === 'error' ? text : null);
    setNotice(kind === 'ok' ? text : null);
  };

  const handleDeleteAdmin = async (admin: AdminUser) => {
    if (!window.confirm(`确定删除管理员「${admin.name}（${admin.email}）」吗？\n删除后会停用该账号并撤销其全部会话。`)) {
      return;
    }
    try {
      await deleteAdminUser(admin.id);
      flash('ok', `已删除 ${admin.name}`);
      admins.reload();
    } catch (e) {
      flash('error', e instanceof Error ? e.message : '删除失败');
    }
  };

  const handleRevokeKey = async (key: ApiKey) => {
    try {
      await revokeApiKey(key.id);
      flash('ok', `已撤销 API Key「${key.name}」`);
      apiKeys.reload();
    } catch (e) {
      flash('error', e instanceof Error ? e.message : '操作失败');
    }
  };

  const handleKickSession = async (session: Session) => {
    try {
      await revokeSession(session.id);
      flash('ok', '会话已踢下线');
      sessions.reload();
    } catch (e) {
      flash('error', e instanceof Error ? e.message : '操作失败');
    }
  };

  const handleRevokeAll = async () => {
    try {
      const { revoked } = await revokeAllSessions();
      flash('ok', `已撤销 ${revoked} 个会话（当前会话保留）`);
      sessions.reload();
    } catch (e) {
      flash('error', e instanceof Error ? e.message : '操作失败');
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">安全设置</h1>
          <p className="page-head__sub">
            管理员、API Key 与会话管理。管理员与 API Key 操作仅 owner 可执行。
          </p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error" role="alert">
          <Icon name="octagon-x" size={ICON_SIZE.inline} />
          {error}
        </div>
      )}
      {notice && (
        <div className="notice notice--ok" role="status">
          <Icon name="circle-check" size={ICON_SIZE.inline} />
          {notice}
        </div>
      )}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tabs__btn ${tab === t.key ? 'tabs__btn--active' : ''}`}
            onClick={() => { setTab(t.key); setError(null); setNotice(null); }}
          >
            {t.label}
          </button>
        ))}
        <div className="tabs__spacer" />
        {tab === 'admins' && isOwner && (
          <button type="button" className="btn btn--primary" onClick={() => setShowCreateAdmin(true)}>
            <Icon name="plus" size={ICON_SIZE.inline} />
            添加管理员
          </button>
        )}
        {tab === 'api-keys' && isOwner && (
          <button type="button" className="btn btn--primary" onClick={() => setShowCreateKey(true)}>
            <Icon name="plus" size={ICON_SIZE.inline} />
            创建 API Key
          </button>
        )}
        {tab === 'sessions' && sessionList.some((s) => !s.is_current) && (
          <button type="button" className="btn btn--quiet btn--danger-text" onClick={handleRevokeAll}>
            <Icon name="log-out" size={ICON_SIZE.inline} />
            全部登出
          </button>
        )}
      </div>

      {tab === 'admins' && (
        <>
          {admins.state.kind === 'failed' ? (
            <div className="table-frame">
              <ErrorBlock message={describeError(admins.state.error, '加载管理员')} onRetry={admins.reload} />
            </div>
          ) : admins.state.kind === 'loading' ? (
            <div className="table-frame"><p className="text-muted">加载中…</p></div>
          ) : adminList.length === 0 ? (
            <div className="table-frame">
              <EmptyFirstRun icon="user-cog" description="暂无管理员。添加第一个管理员。" />
            </div>
          ) : (
            <div className="table-frame">
              <table className="table">
                <thead>
                  <tr>
                    <th>姓名</th>
                    <th>邮箱</th>
                    <th>角色</th>
                    <th>状态</th>
                    <th>最后登录</th>
                    <th>创建时间</th>
                    {isOwner && <th className="table__actions-col">操作</th>}
                  </tr>
                </thead>
                <tbody>
                  {adminList.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.name}{admin.id === currentUser?.id && <span className="tag" style={{ marginLeft: 6 }}>当前</span>}</td>
                      <td className="text-muted">{admin.email}</td>
                      <td><span className="tag">{admin.role}</span></td>
                      <td>
                        <span className={`status-dot status-dot--${admin.is_active ? 'active' : 'archived'}`} />
                        {admin.is_active ? '启用' : '已停用'}
                      </td>
                      <td>{admin.last_login_at ? formatDateTime(admin.last_login_at) : '从未登录'}</td>
                      <td>{formatDate(admin.created_at)}</td>
                      {isOwner && (
                        <td className="table__actions-col">
                          {admin.id !== currentUser?.id ? (
                            <span className="row-actions">
                              <button
                                type="button"
                                className="btn btn--quiet"
                                onClick={() => setEditTarget(admin)}
                                title="编辑管理员资料"
                              >
                                <Icon name="pencil" size={ICON_SIZE.inline} />
                                编辑
                              </button>
                              <button
                                type="button"
                                className="btn btn--quiet"
                                onClick={() => setResetTarget(admin)}
                                title="重置该管理员的登录密码（T2）"
                              >
                                <Icon name="key-round" size={ICON_SIZE.inline} />
                                重置密码
                              </button>
                              <button
                                type="button"
                                className="btn btn--quiet btn--danger-text"
                                onClick={() => handleDeleteAdmin(admin)}
                                title="删除该管理员（软删除）"
                              >
                                <Icon name="trash" size={ICON_SIZE.inline} />
                                删除
                              </button>
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'api-keys' && (
        <>
          {apiKeys.state.kind === 'failed' ? (
            <div className="table-frame">
              <ErrorBlock message={describeError(apiKeys.state.error, '加载 API Key')} onRetry={apiKeys.reload} />
            </div>
          ) : apiKeys.state.kind === 'loading' ? (
            <div className="table-frame"><p className="text-muted">加载中…</p></div>
          ) : apiKeyList.length === 0 ? (
            <div className="table-frame">
              <EmptyFirstRun icon="terminal" description="暂无 API Key。创建 API Key 用于程序化访问。" />
            </div>
          ) : (
            <div className="table-frame">
              <table className="table">
                <thead>
                  <tr>
                    <th>名称</th>
                    <th>前缀</th>
                    <th>角色</th>
                    <th>作用域</th>
                    <th>最后使用</th>
                    <th>过期时间</th>
                    <th>创建时间</th>
                    {isOwner && <th className="table__actions-col">操作</th>}
                  </tr>
                </thead>
                <tbody>
                  {apiKeyList.map((key) => (
                    <tr key={key.id}>
                      <td>{key.name}</td>
                      <td><code>{key.key_prefix}…</code></td>
                      <td><span className="tag">{key.role}</span></td>
                      <td>
                        {key.scopes && key.scopes.length > 0 ? (
                          <span className="scope-tags" title={key.scopes.join(', ')}>
                            {key.scopes.slice(0, 4).map((s) => (
                              <span key={s} className="scope-tag">{s}</span>
                            ))}
                            {key.scopes.length > 4 && <span className="text-muted">+{key.scopes.length - 4}</span>}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{key.last_used_at ? formatDateTime(key.last_used_at) : '-'}</td>
                      <td>{key.expires_at ? formatDate(key.expires_at) : <span className="text-muted">永不过期</span>}</td>
                      <td>{formatDate(key.created_at)}</td>
                      {isOwner && (
                        <td className="table__actions-col">
                          <button
                            type="button"
                            className="btn btn--quiet btn--danger-text"
                            onClick={() => handleRevokeKey(key)}
                            title="撤销后密钥立即失效"
                          >
                            <Icon name="octagon-x" size={ICON_SIZE.inline} />
                            撤销
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'sessions' && (
        <>
          {sessions.state.kind === 'failed' ? (
            <div className="table-frame">
              <ErrorBlock message={describeError(sessions.state.error, '加载会话')} onRetry={sessions.reload} />
            </div>
          ) : sessions.state.kind === 'loading' ? (
            <div className="table-frame"><p className="text-muted">加载中…</p></div>
          ) : sessionList.length === 0 ? (
            <div className="table-frame">
              <EmptyFirstRun icon="monitor" description="暂无活跃会话。" />
            </div>
          ) : (
            <div className="table-frame">
              <table className="table">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>用户代理</th>
                    <th>创建时间</th>
                    <th>过期时间</th>
                    <th>状态</th>
                    <th className="table__actions-col">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionList.map((session) => (
                    <tr key={session.id}>
                      <td><code>{session.ip}</code></td>
                      <td className="text-muted" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.user_agent}</td>
                      <td>{formatDateTime(session.created_at)}</td>
                      <td>{formatDateTime(session.expires_at)}</td>
                      <td>
                        {session.is_current ? (
                          <span className="tag" style={{ background: 'var(--st-live)', color: '#fff' }}>当前</span>
                        ) : (
                          <span className="text-muted">历史</span>
                        )}
                      </td>
                      <td className="table__actions-col">
                        {session.is_current ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--quiet btn--danger-text"
                            onClick={() => handleKickSession(session)}
                            title="踢下线"
                          >
                            <Icon name="log-out" size={ICON_SIZE.inline} />
                            踢下线
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showCreateAdmin && (
        <CreateAdminPanel
          onClose={() => setShowCreateAdmin(false)}
          onCreated={() => { setShowCreateAdmin(false); flash('ok', '管理员已创建'); admins.reload(); }}
        />
      )}
      {showCreateKey && (
        <CreateApiKeyPanel
          onClose={() => setShowCreateKey(false)}
          onCreated={() => { flash('ok', 'API Key 已创建'); apiKeys.reload(); }}
        />
      )}
      {editTarget !== null && (
        <EditAdminPanel
          admin={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); flash('ok', `${editTarget.name} 的资料已更新`); admins.reload(); }}
        />
      )}
      {resetTarget !== null && (
        <ResetPasswordDialog
          admin={resetTarget}
          onClose={() => setResetTarget(null)}
          onReset={() => { setResetTarget(null); flash('ok', `${resetTarget.name} 的密码已重置`); }}
        />
      )}
    </>
  );
}

/* ────────────────────────── 创建管理员 ────────────────────────── */

function CreateAdminPanel({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('operator');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = password.length >= PASSWORD_MIN_LENGTH;

  const handleSubmit = async () => {
    if (!email || !name || !passwordValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await createAdminUser({ email, name, password, role });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">添加管理员</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="field">
            <label>邮箱</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" />
          </div>
          <div className="field">
            <label>姓名</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="姓名" />
          </div>
          <div className="field">
            <label>初始密码（至少 {PASSWORD_MIN_LENGTH} 位）</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="初始密码"
              autoComplete="new-password"
            />
            {password.length > 0 && !passwordValid && (
              <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>密码至少 {PASSWORD_MIN_LENGTH} 位，与登录页校验一致</p>
            )}
          </div>
          <div className="field">
            <label>角色</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="owner">Owner（所有者）</option>
              <option value="operator">Operator（操作员）</option>
              <option value="viewer">Viewer（只读）</option>
            </select>
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <button type="button" className="btn btn--primary" disabled={submitting || !email || !name || !passwordValid} onClick={handleSubmit}>
            {submitting ? '创建中…' : '创建管理员'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── 编辑管理员 ────────────────────────── */

function EditAdminPanel({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(admin.name);
  const [role, setRole] = useState<string>(admin.role);
  const [isActive, setIsActive] = useState(admin.is_active);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameValid = name.trim().length > 0;

  const handleSubmit = async () => {
    if (!nameValid || submitting) return;
    const payload: { name: string; role: string; is_active?: boolean } = {
      name: name.trim(),
      role,
    };
    if (isActive !== admin.is_active) {
      payload.is_active = isActive;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateAdminUser(admin.id, payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">编辑管理员</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="field">
            <label>邮箱</label>
            <input type="email" value={admin.email} disabled />
            <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>邮箱不可修改，用于登录</p>
          </div>
          <div className="field">
            <label>姓名</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="姓名" />
          </div>
          <div className="field">
            <label>角色</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="owner">Owner（所有者）</option>
              <option value="operator">Operator（操作员）</option>
              <option value="viewer">Viewer（只读）</option>
            </select>
          </div>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              {' '}账号启用
            </label>
            <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
              取消勾选会停用该账号并撤销其全部会话（最后一个 owner 不可停用）。
            </p>
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !nameValid}
            onClick={handleSubmit}
          >
            {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── 重置管理员密码（T2） ────────────────────────── */

function ResetPasswordDialog({
  admin,
  onClose,
  onReset,
}: {
  admin: AdminUser;
  onClose: () => void;
  onReset: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = password.length >= PASSWORD_MIN_LENGTH;
  const match = confirm === password && password.length > 0;

  const handleSubmit = async () => {
    if (!passwordValid || !match || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateAdminUser(admin.id, { reset_password: password });
      onReset();
    } catch (e) {
      setError(e instanceof Error ? e.message : '重置失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">重置密码</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">重置 {admin.name}（{admin.email}）的登录密码。该操作会写入审计。</p>
          <div className="field">
            <label>新密码（至少 {PASSWORD_MIN_LENGTH} 位）</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="新密码"
              autoComplete="new-password"
            />
          </div>
          <div className="field">
            <label>确认新密码</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="再次输入新密码"
              autoComplete="new-password"
            />
            {confirm.length > 0 && !match && (
              <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>两次输入不一致</p>
            )}
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !passwordValid || !match}
            onClick={handleSubmit}
          >
            {submitting ? '重置中…' : '确认重置'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CreateApiKeyPanel({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('operator');
  const [expiresAt, setExpiresAt] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ReadonlySet<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ plaintext: string; name: string } | null>(null);

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  useEffect(() => {
    if (!created) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [created]);

  const handleSubmit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const expires_at = expiresAt === '' ? null : Math.floor(new Date(`${expiresAt}T23:59:59`).getTime() / 1000);
      const result = await createApiKey({ name: name.trim(), role, expires_at, scopes: [...selectedScopes] });
      setCreated({ plaintext: result.plaintext, name: result.api_key.name });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
      setSubmitting(false);
    }
  };

  const plaintext = created?.plaintext ?? null;

  return (
    <div className="panel-overlay" onClick={created ? undefined : onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">{created ? 'API Key 已创建' : '创建 API Key'}</h2>
          {!created && (
            <button type="button" className="btn btn--quiet" onClick={onClose}>
              <Icon name="x" size={ICON_SIZE.control} />
            </button>
          )}
        </div>
        <div className="panel__body">
          {plaintext === null ? (
            <>
              <div className="field">
                <label>名称</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="如：发卡站兑付" />
              </div>
              <div className="field">
                <label>角色</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="owner">Owner（所有者）</option>
                  <option value="operator">Operator（操作员）</option>
                  <option value="viewer">Viewer（只读）</option>
                </select>
              </div>
              <div className="field">
                <label>过期时间（可选，留空 = 永不过期）</label>
                <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>
              <div className="field">
                <label>作用域（资源 × 读写，可选）</label>
                <div className="scope-matrix">
                  {SCOPE_MATRIX.map((row) => (
                    <div key={row.resource} className="scope-matrix__row">
                      <span className="scope-matrix__label">{row.label}</span>
                      <span className="scope-matrix__ops">
                        {row.ops.map((op) => {
                          const scope = `${row.resource}:${op}`;
                          return (
                            <label key={scope} className="scope-matrix__check">
                              <input type="checkbox" checked={selectedScopes.has(scope)} onChange={() => toggleScope(scope)} />
                              {op === 'read' ? '读' : '写'}
                            </label>
                          );
                        })}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  作用域为元数据记录（学自 Stripe RAK）。MVP 阶段鉴权仍按角色，scope 级鉴权 v2 接入。
                </p>
              </div>
              {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
              <button type="button" className="btn btn--primary" disabled={submitting || !name.trim()} onClick={handleSubmit}>
                {submitting ? '创建中…' : '创建 API Key'}
              </button>
            </>
          ) : (
            <div className="notice notice--warn" role="alert">
              <Icon name="circle-alert" size={ICON_SIZE.inline} />
              <div>
                <p>
                  <strong>「{created!.name}」</strong>的明文只在此处显示一次，关闭后不可再见。服务端只保存 HMAC 摘要，不保存明文。
                </p>
                <p className="plaintext-box">
                  <code className="plaintext-code">{plaintext}</code>
                  <button
                    type="button"
                    className="btn btn--quiet btn--icon"
                    aria-label="复制"
                    title="复制"
                    onClick={() => navigator.clipboard?.writeText(plaintext)}
                  >
                    <Icon name="copy" size={ICON_SIZE.inline} />
                  </button>
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => navigator.clipboard?.writeText(plaintext).then(() => onClose())}
                  >
                    复制并关闭
                  </button>
                  <button type="button" className="btn btn--secondary" onClick={onClose}>
                    我已复制
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
